import { useEffect, useRef, useState } from "react";

const AIAnimation = () => {
  const canvasRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const updateDimensions = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);

    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = dimensions.width;
    canvas.height = dimensions.height;

    // Neural network node
    class Node {
      constructor(x, y, layer) {
        this.x = x;
        this.y = y;
        this.layer = layer;
        this.radius = 4;
        this.pulse = Math.random() * Math.PI * 2;
        this.pulseSpeed = 0.02 + Math.random() * 0.02;
        this.connections = [];
      }

      update() {
        this.pulse += this.pulseSpeed;
      }

      draw() {
        const pulseSize = Math.sin(this.pulse) * 2;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius + pulseSize, 0, Math.PI * 2);
        
        // Gradient for nodes
        const gradient = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.radius + pulseSize);
        gradient.addColorStop(0, 'rgba(139, 92, 246, 0.8)');
        gradient.addColorStop(1, 'rgba(99, 102, 241, 0.4)');
        
        ctx.fillStyle = gradient;
        ctx.fill();
        
        // Outer glow
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius + pulseSize + 2, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(139, 92, 246, ${0.3 + Math.sin(this.pulse) * 0.2})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    }

    // Create neural network structure
    const layers = 4;
    const nodesPerLayer = 6;
    const nodes = [];
    const layerSpacing = canvas.width / (layers + 1);
    const nodeSpacing = canvas.height / (nodesPerLayer + 1);

    for (let layer = 0; layer < layers; layer++) {
      for (let i = 0; i < nodesPerLayer; i++) {
        const x = layerSpacing * (layer + 1);
        const y = nodeSpacing * (i + 1);
        nodes.push(new Node(x, y, layer));
      }
    }

    // Create connections between layers
    nodes.forEach((node, index) => {
      if (node.layer < layers - 1) {
        const nextLayerNodes = nodes.filter(n => n.layer === node.layer + 1);
        nextLayerNodes.forEach(nextNode => {
          if (Math.random() > 0.3) { // 70% chance of connection
            node.connections.push({
              target: nextNode,
              strength: Math.random(),
              phase: Math.random() * Math.PI * 2,
              speed: 0.01 + Math.random() * 0.02
            });
          }
        });
      }
    });

    // Data pulse effect
    class DataPulse {
      constructor(startNode, connection) {
        this.startNode = startNode;
        this.connection = connection;
        this.progress = 0;
        this.speed = 0.01 + Math.random() * 0.01;
        this.size = 2 + Math.random() * 2;
      }

      update() {
        this.progress += this.speed;
        return this.progress >= 1;
      }

      draw() {
        const x = this.startNode.x + (this.connection.target.x - this.startNode.x) * this.progress;
        const y = this.startNode.y + (this.connection.target.y - this.startNode.y) * this.progress;
        
        ctx.beginPath();
        ctx.arc(x, y, this.size, 0, Math.PI * 2);
        
        const gradient = ctx.createRadialGradient(x, y, 0, x, y, this.size);
        gradient.addColorStop(0, 'rgba(236, 72, 153, 0.9)');
        gradient.addColorStop(1, 'rgba(236, 72, 153, 0)');
        
        ctx.fillStyle = gradient;
        ctx.fill();
      }
    }

    const dataPulses = [];

    // Animation loop
    let animationFrameId;
    let frameCount = 0;

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw connections with animated flow
      nodes.forEach(node => {
        node.connections.forEach(conn => {
          conn.phase += conn.speed;
          
          const dx = conn.target.x - node.x;
          const dy = conn.target.y - node.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          // Animated gradient along connection
          const gradient = ctx.createLinearGradient(node.x, node.y, conn.target.x, conn.target.y);
          const flowPosition = (Math.sin(conn.phase) + 1) / 2;
          
          gradient.addColorStop(0, `rgba(99, 102, 241, ${0.1 * conn.strength})`);
          gradient.addColorStop(flowPosition, `rgba(139, 92, 246, ${0.3 * conn.strength})`);
          gradient.addColorStop(1, `rgba(99, 102, 241, ${0.1 * conn.strength})`);
          
          ctx.beginPath();
          ctx.moveTo(node.x, node.y);
          ctx.lineTo(conn.target.x, conn.target.y);
          ctx.strokeStyle = gradient;
          ctx.lineWidth = 1 + conn.strength;
          ctx.stroke();
        });
      });

      // Update and draw nodes
      nodes.forEach(node => {
        node.update();
        node.draw();
      });

      // Create new data pulses randomly
      if (frameCount % 30 === 0 && dataPulses.length < 15) {
        const randomNode = nodes[Math.floor(Math.random() * nodes.length)];
        if (randomNode.connections.length > 0) {
          const randomConnection = randomNode.connections[Math.floor(Math.random() * randomNode.connections.length)];
          dataPulses.push(new DataPulse(randomNode, randomConnection));
        }
      }

      // Update and draw data pulses
      for (let i = dataPulses.length - 1; i >= 0; i--) {
        const pulse = dataPulses[i];
        if (pulse.update()) {
          dataPulses.splice(i, 1);
        } else {
          pulse.draw();
        }
      }

      frameCount++;
      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [dimensions]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none"
      style={{ opacity: 0.4 }}
    />
  );
};

export default AIAnimation;
